// Data glue for the engineer PWA offline loop: ties the IndexedDB adapter
// (db.ts), the pure sync orchestration (sync.ts) and the API client together.
// The React layer calls these; it never touches IndexedDB or fetch directly.
import { api, ApiError } from "../api";
import type { JobView, OutboxItem } from "./types";
import type { CompletionPayload } from "./types";
import { cacheJobs, getCachedJob, getCachedJobs, outboxStore, updateCachedJob } from "./db";
import { withTapNoted, type PopTagEnd } from "../popTagHome";
import {
  flushOutbox,
  mergeJobsForDisplay,
  classifySendError,
  escalatedSubmissions,
  escalationOfficeMessage,
  escalationReason,
  isEscalated,
  clearEscalation,
} from "./sync";
import type { SendFn, FlushSummary, EscalatedSubmission } from "./sync";
import { enqueueMessage, syncMessages } from "./messagesStore";
import { wipeGeneration } from "./wipeEpoch";
import { addSurveyPhotoNotice, surveySkippedNotice, type SkippedSurveyEntry } from "./surveyStore";

const BG_SYNC_TAG = "ele-sync-jobs";

// Pull the engineer's jobs from the server and replace the offline cache. Only
// call when online; the caller falls back to the cache on failure.
//
// Session ASB review (I6-6): an answer that lands after this browser's offline copies were wiped (a sign-out while the
// request was in flight) is dropped, never written back to the phone (wipeEpoch.ts).
export async function refreshFromServer(): Promise<void> {
  const started = wipeGeneration();
  const jobs = await api.jobs.mine();
  if (wipeGeneration() !== started) return;
  await cacheJobs(jobs);
}

// Session APG: remember on the cached job that a POP tag tap has just landed for it, so the job sheet
// knows before the next refresh and with no signal to refresh with. The server already holds the tap
// and derives the same two instants for every job-list read (job.routes.ts /mine); this is only the
// device catching up early, by the identical rule (withTapNoted: earliest arrival, latest departure).
// A job that is no longer cached, or that already carries this end, is left exactly as it was.
export async function noteTapOnCachedJob(jobId: string, end: PopTagEnd, at: string): Promise<void> {
  // One transaction (updateCachedJob), so a refresh landing at the same moment cannot have this write
  // back a stale copy of the job it is changing.
  await updateCachedJob(jobId, (job) => withTapNoted(job, end, at));
}

// Session BDM: the device catching up early on a working day's times it has just saved (or queued), so the panel and the day lists show
// them at once, offline too. The server holds the same record (or receives it at the next sync) and hands it back on the next refresh.
export async function noteWorkDayTimeOnCachedJob(
  jobId: string,
  day: string,
  patch: { arrivedAt?: string | null; leftAt?: string | null; note?: string | null; overtimeBooked?: boolean },
): Promise<void> {
  await updateCachedJob(jobId, (job) => {
    if (!job.workDays) return job;
    return {
      ...job,
      workDays: job.workDays.map((d) =>
        d.day === day
          ? {
              ...d,
              time: {
                arrivedAt: patch.arrivedAt !== undefined ? patch.arrivedAt : (d.time?.arrivedAt ?? null),
                leftAt: patch.leftAt !== undefined ? patch.leftAt : (d.time?.leftAt ?? null),
                note: patch.note !== undefined ? patch.note : (d.time?.note ?? null),
                overtimeBooked: patch.overtimeBooked !== undefined ? patch.overtimeBooked : (d.time?.overtimeBooked ?? false),
                // The office's yes was to specific hours (Session OTA): a changed arrival or departure, or unbooking, takes it away on the
                // phone at once, as the server does when the save arrives. A note alone keeps it.
                overtimeApproved:
                  (patch.overtimeBooked ?? d.time?.overtimeBooked ?? false) &&
                  (patch.arrivedAt === undefined || patch.arrivedAt === (d.time?.arrivedAt ?? null)) &&
                  (patch.leftAt === undefined || patch.leftAt === (d.time?.leftAt ?? null))
                    ? (d.time?.overtimeApproved ?? false)
                    : false,
              },
            }
          : d,
      ),
    };
  });
}

// The list the UI renders: cached jobs with queued completions overlaid. Works
// fully offline (reads IndexedDB only).
//
// `pending` counts only what is still GOING to sync. An escalated submission is excluded and
// reported separately: telling the engineer "1 to sync" about something that will never sync on
// its own is the dishonest state this session exists to remove.
export async function loadMerged(): Promise<{
  jobs: JobView[];
  pending: number;
  escalations: EscalatedSubmission[];
}> {
  const [cached, outbox] = await Promise.all([getCachedJobs(), outboxStore.getOutbox()]);
  return {
    jobs: mergeJobsForDisplay(cached, outbox),
    pending: outbox.filter((i) => !isEscalated(i)).length,
    escalations: escalatedSubmissions(cached, outbox),
  };
}

// Queue a completion. Persists to the outbox immediately (survives a reload /
// the app being killed in a basement), so it is never lost before it syncs.
export async function enqueueCompletion(jobId: string, payload: CompletionPayload): Promise<void> {
  await outboxStore.putOutbox({ id: jobId, payload, queuedAt: new Date().toISOString(), attempts: 0 });
}

// Send one queued completion. A 2xx clears it; a 404/410 (job gone) drops it; a
// network error throws (flushOutbox treats it as retryable); any other status is
// kept and surfaced as a sync error -- nothing is discarded silently.
// The server's error code rides along as the detail so an escalated item carries something the
// office can diagnose from (it is never shown to the engineer raw).
const send: SendFn = async (item) => {
  try {
    const res = (await api.jobs.complete(item.id, item.payload)) as { projectSurvey?: { skipped?: SkippedSurveyEntry[] } } | undefined;
    // Session PJ2 review, HIGH 2: a survey visit's sheet is accepted even when one of its surveys was deleted, moved off the visit or
    // replaced by a resurvey while the phone was offline; those entries were not applied, and the engineer is told which, by name.
    for (const s of res?.projectSurvey?.skipped ?? []) addSurveyPhotoNotice(surveySkippedNotice(s));
    return { ok: true };
  } catch (err) {
    if (err instanceof ApiError) return classifySendError(err.status, err.code);
    throw err; // network/offline -> retryable
  }
};

// Session AEK (D32): an escalation is a real operational event, so it is logged as an ERROR with
// structure rather than left silent. This is the only place the raw status is written out.
function logEscalation(item: OutboxItem): void {
  console.error("[ele-sync] submission escalated: needs the office", {
    jobId: item.id,
    httpStatus: item.escalatedStatus ?? null,
    attempts: item.attempts,
    structuralFailures: item.structuralFailures ?? 0,
    queuedAt: item.queuedAt,
    escalatedAt: item.escalatedAt ?? null,
    lastError: item.lastError ?? null,
  });
}

// Session AEK (D32, A4): tell the OFFICE about an escalation, not just the engineer.
//
// The notice is queued into the EXISTING message outbox, so it inherits the whole offline loop:
// it survives being written with no signal, it is retried until the server accepts it, and it is
// never dropped. It lands in the office's message thread for that engineer, which is durable and
// already carries an unread badge, so nothing new has to be watched.
//
// Written as a SWEEP rather than a one-shot at the moment of escalation, deliberately: an
// escalated item is skipped by every later drain, so a single failed attempt to notify would
// otherwise mean the office was never told at all. The sweep re-tries until it sticks, and
// `officeNotifiedAt` keeps it to once per escalation.
async function notifyOfficeOfEscalations(): Promise<void> {
  let items: OutboxItem[];
  try {
    items = await outboxStore.getOutbox();
  } catch {
    return;
  }

  let queued = false;
  for (const snapshot of items) {
    if (!isEscalated(snapshot) || snapshot.officeNotifiedAt) continue;
    try {
      // Re-read immediately before writing. The snapshot above can be stale: this app's drain
      // guard is per-JS-context, and the service worker pings EVERY open client, so a second tab
      // can have notified between the read and here. Re-reading narrows that window to almost
      // nothing, and a duplicate notice in the office's thread is the thing worth avoiding.
      const item = (await outboxStore.getOutbox()).find((i) => i.id === snapshot.id);
      if (!item || !isEscalated(item) || item.officeNotifiedAt) continue;

      // Claim it FIRST, then send. If the claim succeeds and the send fails we release the claim
      // below and try again next drain; doing it the other way round risks sending the notice and
      // then failing to record that we did, which duplicates it. A retried flag beats a duplicate.
      await outboxStore.putOutbox({ ...item, officeNotifiedAt: new Date().toISOString() });
      try {
        const job = await getCachedJob(item.id);
        await enqueueMessage({
          body: escalationOfficeMessage({
            eleNumber: job?.eleNumber,
            jobNumber: job?.jobNumber,
            siteName: job?.siteName,
            reason: escalationReason(item.escalatedStatus, item.escalatedCode),
          }),
          jobId: item.id,
        });
        queued = true;
      } catch (err) {
        await outboxStore.putOutbox(item); // release the claim so the next drain retries
        throw err;
      }
    } catch {
      // Best effort, and retried on the next drain. The engineer's own amber banner is the
      // primary signal and is already showing, so a failure here never leaves them uninformed.
    }
  }

  // Send it now rather than waiting for the next reconnect or foreground. The message drain runs
  // alongside this one (EngineerDataContext fires both together), so a notice queued here would
  // otherwise sit until the drain after next, which could be hours. An escalation is an
  // operational alert, so the office should get it on the connection that produced it. Safe to
  // call: syncMessages has its own concurrency guard and is a no-op if a drain is already running.
  if (queued) {
    try {
      await syncMessages();
    } catch {
      // The notice is durably queued either way; the next drain will send it.
    }
  }
}

// Drain the outbox. Guarded so overlapping triggers (reconnect + foreground +
// the service worker's background-sync message) never run concurrently.
let flushing = false;
export async function syncJobs(): Promise<FlushSummary | null> {
  if (flushing) return null;
  flushing = true;
  try {
    // The notify runs in a finally, so an IndexedDB write failure part-way through the drain
    // cannot also swallow the office's only notification of an escalation recorded earlier.
    try {
      return await flushOutbox(outboxStore, send, { onEscalate: logEscalation });
    } finally {
      await notifyOfficeOfEscalations();
    }
  } finally {
    flushing = false;
  }
}

// The engineer's way out of an escalation (Session AEK, the recovery path). Puts the submission
// back in the retry loop and drains immediately, so once the office says a record is sorted the
// engineer can send the sheet from the job page rather than the work being stranded for good.
export async function retryEscalated(jobId: string): Promise<void> {
  const item = (await outboxStore.getOutbox()).find((i) => i.id === jobId);
  if (!item || !isEscalated(item)) return;
  await outboxStore.putOutbox(clearEscalation(item));
  await syncJobs();
}

// Best-effort: ask the browser to fire a background sync when connectivity
// returns, even if the app is backgrounded. A no-op where unsupported (e.g. iOS
// Safari) -- the reconnect/foreground triggers cover those cases.
export async function requestBackgroundSync(): Promise<void> {
  try {
    if (!("serviceWorker" in navigator)) return;
    const reg = (await navigator.serviceWorker.ready) as ServiceWorkerRegistration & {
      sync?: { register: (tag: string) => Promise<void> };
    };
    await reg.sync?.register(BG_SYNC_TAG);
  } catch {
    // Unsupported or denied -- ignore; the app-driven flush is the reliable path.
  }
}
